import { AbstractParser } from "../../parser";
import { User } from "src/app/ui/models/domains/pbm/user/user";
import { LoginInfo } from "src/app/ui/models/domains/pbm/user/login-info";
import { UserCompanyInfo } from "src/app/ui/models/domains/pbm/user/user-company-info";
import { UserRole } from "src/app/ui/models/domains/pbm/user/user-role";
import { UserPermission } from "src/app/ui/models/domains/pbm/user/user-permission";
import { Company } from "src/app/ui/models/domains/pbm/companies/company";
import { CompanyParser } from "../company/company.parser";
import { UserTenant } from "src/app/ui/models/domains/pbm/user/user-tenant";

export class UserCredentialParser extends AbstractParser<any> {
  parse(payload: any) {
    return {
      user: this.parseUser(payload.User),
      loginInfo: this.parseLoginInfo(payload.LoginInfo),
      userTenants: this.parseTenants(payload.UserTenants),
    };
  }

  private parseTenants(payload: any[]): UserTenant[] {
    if (!Array.isArray(payload)) return [];
    return payload.map((tenant) => ({
      id: tenant.Id,
      key: tenant.Key,
      description: tenant.Description,
      useDataQuality: tenant.UseDataQuality,
      useBenefitDiscount: tenant.UseBenefitDiscount,
      tenantStyle: tenant.tenantStyle
        ? {
            imagePath: tenant.tenantStyle.ImagePath,
            buttonBackgroundColor: tenant.tenantStyle.ButtonBackgroundColor,
            fontColor: tenant.tenantStyle.FontColor,
          }
        : {
            imagePath: "",
            buttonBackgroundColor: "#9389fb",
            fontColor: "#fefefe",
          },
    }));
  }

  private parseUser(payload: any): User {
    const user = new User();
    user.setUserKey(payload?.UserKey?.Value ?? "");
    user.setActivate(payload?.Active ?? false);
    user.setName(payload?.Name ?? "");
    user.setEmail(payload?.Email ?? "");
    user.setUserCompanyInfo(this.parseUserCompanyInfo(payload?.CompanyInfo));
    user.setAllowedCompanies(this.parseAllowedCompanies(payload?.AllowedCompanies));
    user.setUserRoleList(this.parseUserRoleList(payload?.UserRoles));
    return user;
  }

  private parseUserCompanyInfo(payload: any): UserCompanyInfo {
    const info = new UserCompanyInfo();
    info.setTelephone(payload?.Telephone ?? "");
    info.setFiscalNumber(payload?.FiscalNumber ?? "");
    info.setCorporateName(payload?.CorporateName ?? "");
    info.setHealthProfessional(payload?.HealthProfessional ?? "");
    info.setClassCouncil(payload?.ClassCouncil ?? "");
    info.setRegistrationNumber(payload?.RegistrationNumber ?? "");
    return info;
  }

  private parseAllowedCompanies(payload: any[]): Company[] {
    if (!Array.isArray(payload)) return [];
    const companyParser = new CompanyParser();
    return payload.map((p) => companyParser.parse(p));
  }

  private parseUserRoleList(payload: any[]): UserRole[] {
    if (!Array.isArray(payload)) return [];
    return payload.map((role) => {
      const userRole = new UserRole();
      userRole.setUserRoleKey(role?.UserRoleKey?.Value ?? "");
      userRole.setName(role?.Name ?? "");
      userRole.setUserPermissionList(this.parseUserPermissionList(role?.UserPermissions));
      userRole.setUserRoleType(role?.UserRoleType ?? "");
      return userRole;
    });
  }

  private parseUserPermissionList(payload: any[]): UserPermission[] {
    if (!Array.isArray(payload)) return [];
    return payload.map((perm) => {
      const userPermission = new UserPermission();
      userPermission.setName(perm?.Name ?? "");
      userPermission.setCode(perm?.Code ?? "");
      userPermission.setUserRoleType(perm?.UserRoleType ?? "");
      return userPermission;
    });
  }

  private parseLoginInfo(payload: any): LoginInfo {
    const info = new LoginInfo();
    info.setAccessToken(payload?.access_token ?? "");
    info.setTokenType(payload?.token_type ?? "");
    info.setExpiresIn(payload?.expires_in ?? 0);
    info.setRefreshToken(payload?.refresh_token ?? "");
    info.setUserId(payload?.userId ?? "");
    info.setuserName(payload?.userName ?? "");
    return info;
  }
}
